import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gauge, Package, Navigation, CheckCircle2, AlertTriangle, Star, MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

function ScoreRing({ value }) {
  const v = Math.max(0, Math.min(100, value || 0));
  const color = v >= 80 ? "#059669" : v >= 60 ? "#d97706" : "#dc2626";
  return (
    <div className="relative h-28 w-28">
      <svg viewBox="0 0 36 36" className="h-28 w-28 -rotate-90">
        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
        <circle cx="18" cy="18" r="15.9" fill="none" stroke={color} strokeWidth="3"
          strokeDasharray={`${v}, 100`} strokeLinecap="round" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-2xl font-bold text-slate-900">{v}</span>
        <span className="text-[10px] uppercase tracking-wider text-slate-400">/ 100</span>
      </div>
    </div>
  );
}

function JobList({ title, orders, testid }) {
  const { t } = useI18n();
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid={testid}>
      <h3 className="font-display text-lg font-semibold text-slate-900 mb-3">{title} <span className="text-slate-400 text-sm">({orders.length})</span></h3>
      <div className="space-y-2">
        {orders.length === 0 && <p className="text-sm text-slate-400">{t("common.none")}</p>}
        {orders.map((o) => (
          <Link key={o.id} to={`/orders/${o.id}`} className="flex items-center justify-between p-2 -mx-2 rounded-lg hover:bg-slate-50" data-testid="driver-job-row">
            <div>
              <div className="text-sm font-medium text-slate-800">{o.number}</div>
              <div className="text-xs text-slate-400">{o.origin} → {o.destination}</div>
            </div>
            <StatusBadge status={o.status} />
          </Link>
        ))}
      </div>
    </div>
  );
}

export function DriverDashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard/driver").then(({ data }) => setData(data)).catch(() => setData({ orders: { active: [], future: [], completed: [] }, score: null })); }, []);

  if (!data) return <div className="text-slate-400 py-10">{t("common.loading")}</div>;
  const sc = data.score || {};
  const st = sc.stats || {};
  const firstActive = (data.orders?.active || [])[0] || (data.orders?.future || [])[0];

  return (
    <div className="space-y-6" data-testid="driver-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">{t("dd.welcome")}, {user?.name}</h1>
          <p className="text-sm text-slate-500 mt-1 capitalize">{user?.role}</p>
        </div>
        <Link to={firstActive ? `/orders/${firstActive.id}` : "/orders"}>
          <Button variant="outline" data-testid="driver-messages-btn"><MessageSquare size={16} className="mr-1.5" /> {t("dd.messages")}</Button>
        </Link>
      </div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="driver-score-card">
        <h3 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2"><Gauge size={18} /> {t("dd.myScore")}</h3>
        <div className="flex flex-wrap items-center gap-8">
          <ScoreRing value={sc.final_score} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-8 gap-y-3 text-sm">
            <Metric label={t("dd.systemScore")} value={sc.system_score ?? "—"} testid="driver-system-score" />
            <Metric label={t("dd.adminAdj")} value={(sc.admin_adjustment ?? 0) > 0 ? `+${sc.admin_adjustment}` : (sc.admin_adjustment ?? 0)} testid="driver-admin-adj" />
            <Metric label={t("dd.finalScore")} value={sc.final_score ?? "—"} testid="driver-final-score" accent />
            <Metric label={t("dd.onTime")} value={`${Math.round((st.on_time_rate || 0) * 100)}%`} />
            <Metric label={t("dd.pod")} value={`${Math.round((st.pod_rate || 0) * 100)}%`} />
            <Metric label={t("dd.deliveries")} value={st.delivered ?? 0} />
          </div>
          {sc.admin_rating ? (
            <div className="flex items-center gap-1 ml-auto" data-testid="driver-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <Star key={n} size={18} className={n <= sc.admin_rating ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
              ))}
            </div>
          ) : null}
        </div>
        <div className="mt-4 flex flex-wrap gap-2 text-xs">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1"><CheckCircle2 size={13} /> {t("dd.completed")}: {st.completed ?? 0}</span>
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-3 py-1"><AlertTriangle size={13} /> {t("dd.incidents")}: {st.incidents ?? 0}</span>
        </div>
        {(sc.notes || []).length > 0 && (
          <div className="mt-4 border-t border-slate-100 pt-3 space-y-1" data-testid="driver-score-notes">
            {sc.notes.slice().reverse().map((n, i) => (
              <p key={i} className="text-xs text-slate-500">“{n.note}” — {n.by}</p>
            ))}
          </div>
        )}
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-6">
        <JobList title={t("dd.activeJobs")} orders={data.orders.active} testid="driver-active-jobs" />
        <JobList title={t("dd.future")} orders={data.orders.future} testid="driver-future-jobs" />
        <JobList title={t("dd.completed")} orders={data.orders.completed} testid="driver-completed-jobs" />
      </div>
    </div>
  );
}

function Metric({ label, value, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`mt-0.5 font-semibold ${accent ? "text-blue-900 text-lg" : "text-slate-800"}`}>{value}</div>
    </div>
  );
}
